import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import BaseUrl from "../Components/BaseUrl";
import PropTypes from "prop-types";
import { Search, ChevronDown, ChevronRight, Check, X, Users } from "lucide-react";
import Swal from "sweetalert2";
const Avatar = ({ src, alt, size = "medium" }) => {
  const sizeClasses = {
    small: "w-8 h-8",
    medium: "w-12 h-12",
    large: "w-16 h-16",
  };

  return (
    <div
      className={`${sizeClasses[size]} rounded-full overflow-hidden flex-shrink-0`}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.src = `https://ui-avatars.com/api/?name=${alt.charAt(
            0
          )}&background=5566c2&color=fff&size=128`;
        }}
      />
    </div>
  );
};

const Button = ({
  children,
  variant = "primary",
  size = "medium",
  disabled = false,
  onClick,
  className = "",
}) => {
  const baseClasses =
    "font-medium rounded-full focus:outline-none transition-all duration-200";
  const variantClasses = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300",
    secondary:
      "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400",
  };
  const sizeClasses = {
    small: "px-3 py-1 text-sm",
    medium: "px-4 py-2",
    large: "px-6 py-3 text-lg",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

const SearchBar = ({ value, onChange, placeholder = "Search..." }) => (
  <div className="relative w-full">
    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
      <Search className="h-5 w-5 text-gray-400" />
    </div>
    <input
      type="text"
      className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm placeholder-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 shadow-sm hover:shadow-md"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const FilterButton = ({ label, isActive = false, onClick }) => (
  <button
    className={`flex items-center px-4 py-2 rounded-full border ${
      isActive
        ? "border-blue-600 bg-blue-50 text-blue-600"
        : "border-gray-300 hover:bg-gray-50"
    } transition-colors duration-200`}
    onClick={onClick}
  >
    <span className="mr-1">{label}</span>
    <ChevronDown className="h-4 w-4" />
  </button>
);

const FilterSection = () => {
  const [activeFilters, setActiveFilters] = useState([]);
  const toggleFilter = (filter) => {
    setActiveFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter]
    );
  };

  return (
    <div className="flex flex-wrap gap-2 mt-4 pb-4 border-b border-gray-200">
      <FilterButton
        label="Locations"
        isActive={activeFilters.includes("Locations")}
        onClick={() => toggleFilter("Locations")}
      />
      <FilterButton
        label="Current company"
        isActive={activeFilters.includes("Current company")}
        onClick={() => toggleFilter("Current company")}
      />
      <FilterButton
        label="School"
        isActive={activeFilters.includes("School")}
        onClick={() => toggleFilter("School")}
      />
      <button className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors duration-200">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

const CheckBox = ({ checked, onChange, disabled = false }) => (
  <div
    className={`w-6 h-6 rounded-sm flex items-center justify-center transition-colors duration-200 ${
      disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
    } ${
      checked
        ? "bg-green-600 border border-green-600"
        : "border border-gray-300"
    }`}
    onClick={disabled ? undefined : onChange}
  >
    {checked && <Check className="h-4 w-4 text-white" />}
  </div>
);

const ConnectionItem = ({ connection, onToggle }) => {
  const { user_id, name, headline, profile, already_invited,  } =
    connection;
    const attending = already_invited === "Attending";

  const shouldHideCheckbox = already_invited || attending;

  return (
    <div className="flex items-start gap-3 py-4 px-4 border-b border-gray-200 group hover:bg-gray-50 transition-colors duration-200">
      <div className="w-6 flex justify-center items-start">
        {!shouldHideCheckbox && (
          <CheckBox
            checked={!!connection.isSelected}
            onChange={() => onToggle(user_id)}
          />
        )}
      </div>
      <Avatar src={profile ? profile : "/Images/user-img.jpg"} alt={name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center">
          <h3 className="font-medium text-gray-900">{name}</h3>
          {(already_invited === "Invited"|| already_invited === "Attending") && (
            <span className="ml-auto text-gray-500 text-sm">{already_invited === "Invited" ? "Invited" : "Attending"}</span>
          )}
        </div>
        <p className="text-sm text-gray-600 truncate">{headline}</p>
      </div>
    </div>
  );
};

const ConnectionsList = ({ connections, onToggle }) => (
  <div className="max-h-[400px] overflow-y-auto ">
    {connections.map((connection) => (
      <ConnectionItem
        key={connection.user_id}
        connection={connection}
        onToggle={onToggle}
      />
    ))}
  </div>
);

const SelectedConnectionsList = ({
  connections,
  onUnselect,
  onUnselectAll,
}) => {
  return (
    <div className="p-4 min-w-[200px]">
      <div className="flex justify-between items-center mb-4">
        <span className="text-gray-700 font-medium">
          {connections.length} selected
        </span>
        <button
          className="text-blue-600 hover:underline text-sm"
          onClick={onUnselectAll}
        >
          Unselect all
        </button>
      </div>

      <div className="space-y-3">
        {connections.map((connection) => (
          <div key={connection.user_id} className="flex items-center gap-2">
            <Avatar
              src={connection.profile   ? connection.profile : "/Images/user-img.jpg"}
              alt={connection.name}
              size="small"
            />
            <span className="text-sm flex-1 truncate">{connection.name}</span>
            <button
              className="text-gray-400 hover:text-gray-600"
              onClick={() => onUnselect(connection.user_id)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

Avatar.propTypes = {
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    size: PropTypes.oneOf(["small", "medium", "large"]),
  };
  
  Button.propTypes = {
    children: PropTypes.node.isRequired,
    variant: PropTypes.oneOf(["primary", "secondary"]),
    size: PropTypes.oneOf(["small", "medium", "large"]),
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    className: PropTypes.string,
  };
  
  SearchBar.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
  };
  
  FilterButton.propTypes = {
    label: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
    onClick: PropTypes.func,
  };
  
  CheckBox.propTypes = {
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
  };
  
  ConnectionItem.propTypes = {
    connection: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      avatar: PropTypes.string.isRequired,
      isSelected: PropTypes.bool,
      isOwner: PropTypes.bool,
    }).isRequired,
    onToggle: PropTypes.func.isRequired,
  };
  
  ConnectionsList.propTypes = {
    connections: PropTypes.arrayOf(PropTypes.object).isRequired,
    onToggle: PropTypes.func.isRequired,
  };
  
  SelectedConnectionsList.propTypes = {
    connections: PropTypes.arrayOf(PropTypes.object).isRequired,
    onUnselect: PropTypes.func.isRequired,
    onUnselectAll: PropTypes.func.isRequired,
  };
  

  

const InviteEventConnectionModal = ({ isOpen, onClose,connection,slug,getConnections,getInvitedEventUsers }) => {
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    if (connection && Array.isArray(connection)) {
      const initializedConnections = connection.map((c) => ({
        ...c,
        isSelected: false,
      }));
      setConnections(initializedConnections);
    }
  }, [connection]);
  

  const [searchQuery, setSearchQuery] = useState("");

  const toggleSelection = useCallback((user_id) => {
    setConnections((prev) =>
      prev.map((connection) =>
        connection.user_id === user_id && !connection.isOwner
          ? { ...connection, isSelected: !connection.isSelected }
          : connection
      )
    );
  }, []);

  const unselectAll = useCallback(() => {
    setConnections((prev) =>
      prev.map((connection) => ({ ...connection, isSelected: false }))
    );
  }, []);

  const filteredConnections = useMemo(() => {
    if (!searchQuery) return connections;
    return connections.filter((connection) =>
      connection.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [connections, searchQuery]);

  const selectedConnections = useMemo(
    () => connections.filter((connection) => connection.isSelected),
    [connections]
  );
  const sendInvite = async (userIds) => {
    try {
      const response = await axios.post(
        `${BaseUrl}inviteEventConections/${slug}`,
        {
          users: userIds, 
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            Accept: "application/json",
          },
        }
      );
  
      if (response.data.status === 200) {
        Swal.fire({
          toast: true,
          position: "bottom-start",
          icon: "success",
          title: "Invitation sent successfully!",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        onClose();
        getConnections(); 
        getInvitedEventUsers();
      }
    } catch (error) {
      console.log(error);
    }
  };
  
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-fadeIn">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            Invite connections
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-2 border-b border-gray-300">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name"
          />
        </div>

        <div className="flex flex-1">
          <div className="basis-[70%] overflow-y-auto border-r border-gray-200">
            {/* <FilterSection /> */}
            <ConnectionsList
              connections={filteredConnections}
              onToggle={toggleSelection}
            />
          </div>

          <div className="basis-[30%]">
            <SelectedConnectionsList
              connections={selectedConnections}
              onUnselect={toggleSelection}
              onUnselectAll={unselectAll}
            />
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <Button
            disabled={selectedConnections.length === 0}
            onClick={() =>
              sendInvite(selectedConnections.map((user) => user.user_id))
            }
          >
            Invite{" "}
            
          </Button>
        </div>
      </div>
    </div>
  );
};
export default InviteEventConnectionModal

InviteEventConnectionModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    connection: PropTypes.arrayOf(PropTypes.object).isRequired,
  };